import React, { useState, useEffect, useCallback } from 'react';
import { getProducts, createSale, getCustomers, createCustomer, getCategories } from '../services/api';
import Layout from '../components/Layout';
import { showSuccess, showError, showWarning, showSaleComplete } from '../services/swal';
import { printInvoice } from '../services/printService';

const POS = () => {
    const [products, setProducts] = useState([]);
    const [cart, setCart] = useState([]);
    const [search, setSearch] = useState('');
    const [user] = useState(JSON.parse(localStorage.getItem('user')));
    
    const [customers, setCustomers] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [customerSearch, setCustomerSearch] = useState('');
    const [showNewCustomerModal, setShowNewCustomerModal] = useState(false);
    const [newCustomer, setNewCustomer] = useState({ name: '', phone: '', address: '' });

    const [paymentMethod, setPaymentMethod] = useState('cash');
    const [paidAmount, setPaidAmount] = useState('');
    const [loading, setLoading] = useState(false);
    const [discount, setDiscount] = useState('');

    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('');
    const [activeTab, setActiveTab] = useState('products'); // For mobile view

    const fetchProducts = useCallback(async () => {
        try {
            const params = {
                search,
                category_id: selectedCategory,
                status: 'in_stock'
            };
            const res = await getProducts(params);
            setProducts(res.data.data || []);
        } catch (err) {
            console.error("Failed to fetch products");
            setProducts([]);
        }
    }, [search, selectedCategory]);

    const fetchCategories = useCallback(async () => {
        try {
            const res = await getCategories();
            setCategories(res.data || []);
        } catch (err) {
            console.error("Failed to fetch categories");
        }
    }, []);

    useEffect(() => {
        fetchProducts();
        fetchCategories();
    }, [fetchProducts, fetchCategories]);

    const fetchCustomers = useCallback(async () => {
        try {
            const res = await getCustomers({ search: customerSearch });
            setCustomers(res.data.data || []);
        } catch (err) {
            console.error("Failed to fetch customers");
            setCustomers([]);
        }
    }, [customerSearch]);

    useEffect(() => {
        if (customerSearch.length >= 1) fetchCustomers();
        else setCustomers([]);
    }, [customerSearch, fetchCustomers]);

    const handleCreateCustomer = async () => {
        try {
            const res = await createCustomer(newCustomer);
            setSelectedCustomer(res.data);
            setShowNewCustomerModal(false);
            setNewCustomer({ name: '', phone: '', address: '' });
            showSuccess('Customer Created!', 'New customer has been added.');
        } catch (err) {
            setShowNewCustomerModal(false);
            showError('Failed to Create', err.response?.data?.message || err.message);
        }
    };

    const addToCart = (product) => {
        if (product.quantity <= 0) return;
        const existingItem = cart.find(item => item.product_id === product.id);
        if (existingItem) {
            if (existingItem.quantity >= product.quantity) {
                showWarning('Out of Stock', `No more stock available for ${product.name}.`);
                return;
            }
            setCart(cart.map(item => item.product_id === product.id ? { ...item, quantity: item.quantity + 1 } : item));
        } else {
            setCart([...cart, { 
                product_id: product.id, 
                name: product.name, 
                price: parseFloat(product.unit_price),
                original_price: parseFloat(product.unit_price),
                quantity: 1,
                discount: 0,
                max_quantity: product.quantity
            }]);
        }
    };

    const updateQuantity = (id, delta) => {
        setCart(cart.map(item => {
            if (item.product_id === id) {
                const newQty = Math.max(1, Math.min(item.quantity + delta, item.max_quantity));
                return { ...item, quantity: newQty };
            }
            return item;
        }));
    };

    const handleCartChange = (id, field, value) => {
        const numericValue = parseFloat(value) || 0;
        setCart(cart.map(item => 
            item.product_id === id ? { ...item, [field]: numericValue } : item
        ));
    };

    const removeFromCart = (id) => {
        setCart(cart.filter(item => item.product_id !== id));
    };

    const calculateTotal = useCallback(() => {
        const subtotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
        const disc = parseFloat(discount) || 0;
        return Math.max(0, subtotal - disc).toFixed(0);
    }, [cart, discount]);

    const calculateSubtotal = () => {
        return cart.reduce((sum, item) => sum + (item.price * item.quantity), 0).toFixed(0);
    };

    const handleCheckout = async () => {
        if (cart.length === 0) { showWarning('Cart Empty', 'Please add items to the cart before checkout.'); return; }
        
        const subtotal = parseFloat(calculateSubtotal());
        const discVal = parseFloat(discount) || 0;
        const finalAmount = Math.max(0, subtotal - discVal);

        if (paymentMethod === 'credit') {
            if (!selectedCustomer) { showWarning('Customer Required', 'A customer must be selected for a Credit sale.'); return; }
        } else if (paymentMethod === 'partial') {
            if (!selectedCustomer) { showWarning('Customer Required', 'A customer must be selected for a Partial payment sale.'); return; }
            if (!paidAmount || parseFloat(paidAmount) <= 0 || parseFloat(paidAmount) >= finalAmount) {
                showWarning('Invalid Amount', 'Please enter a valid paid amount for partial payment.');
                return;
            }
        }
        
        setLoading(true);
        const saleData = {
            items: cart,
            customer_id: selectedCustomer?.id,
            customer_name: selectedCustomer?.name || 'Walk-in Customer',
            customer_phone: selectedCustomer?.phone,
            total_amount: subtotal,
            discount: discVal,
            final_amount: finalAmount,
            payment_method: paymentMethod,
            paid_amount: paymentMethod === 'partial' ? parseFloat(paidAmount) : (paymentMethod === 'credit' ? 0 : finalAmount)
        };

        try {
            const res = await createSale(saleData);
            const sale = res.data || res;
            setCart([]);
            setSelectedCustomer(null);
            setCustomerSearch('');
            setPaymentMethod('cash');
            setDiscount('');
            fetchProducts();

            const result = await showSaleComplete(sale.invoice_number || 'N/A', finalAmount.toFixed(2));
            if (result.isConfirmed && sale.invoice_number) {
                const actualPaid = paymentMethod === 'partial' ? parseFloat(paidAmount) : (paymentMethod === 'credit' ? 0 : finalAmount);
                printInvoice({
                    invoice_number: sale.invoice_number,
                    customer_name: selectedCustomer?.name || 'Walk-in Customer',
                    items: sale.items || cart,
                    final_amount: finalAmount,
                    paid_amount: actualPaid,
                    due_amount: finalAmount - actualPaid,
                    date: new Date()
                });
            }
        } catch (err) {
            showError('Checkout Failed', err.response?.data?.message || err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Layout title="Point of Sale">
            <div className="pos-container">
                {/* Left Side: Product Discovery */}
                <div className={`pos-products-section ${activeTab === 'cart' ? 'd-custom-drawer-mode' : ''}`}>
                    <div className="d-flex flex-column gap-3">
                        <div className="position-relative search-wrapper">
                            <i className="bi bi-search search-icon"></i>
                            <input 
                                type="text" 
                                className="form-control pos-search-input" 
                                placeholder="Search items by name or SKU..." 
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>

                        <div className="pos-category-pills custom-scrollbar">
                            <div 
                                className={`pos-category-pill ${selectedCategory === '' ? 'active' : ''}`}
                                onClick={() => setSelectedCategory('')}
                            >
                                All Items
                            </div>
                            {categories.map(cat => (
                                <div 
                                    key={cat.id} 
                                    className={`pos-category-pill ${selectedCategory === cat.id ? 'active' : ''}`}
                                    onClick={() => setSelectedCategory(cat.id)}
                                >
                                    {cat.name}
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="flex-grow-1 overflow-auto custom-scrollbar pe-2" style={{ height: '0' }}>
                        <div className="row g-3">
                            {products.map(p => (
                                <div key={p.id} className="col-6 col-lg-4 col-xl-3">
                                    <div 
                                        className={`pos-product-card ${p.quantity <= 0 ? 'out-of-stock' : ''}`}
                                        onClick={() => addToCart(p)}
                                    >
                                        <div className="product-price">Rs. {parseFloat(p.unit_price).toFixed(0)}</div>
                                        <div className="product-icon fs-1 mb-2">📦</div>
                                        <h6 className="fw-bold mb-1 text-truncate">{p.name}</h6>
                                        <div className="d-flex justify-content-between align-items-center">
                                            <small className="text-muted">{p.sku}</small>
                                            <span className={`badge ${p.quantity > 5 ? 'bg-success' : 'bg-warning'} rounded-pill`}>
                                                {p.quantity} left
                                            </span>
                                        </div>
                                        <div className="product-add-overlay">
                                            <i className="bi bi-plus-circle-fill"></i>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Right Side: Cart / Checkout */}
                <div className={`pos-cart-section ${activeTab === 'cart' ? 'show-mobile' : ''}`}>
                    <div className="p-4 border-bottom d-flex justify-content-between align-items-center bg-body-tertiary">
                        <div className="d-flex align-items-center gap-2">
                            <button className="btn btn-sm btn-light d-custom-drawer-mode rounded-circle" onClick={() => setActiveTab('products')}>
                                <i className="bi bi-arrow-left"></i>
                            </button>
                            <h5 className="fw-bold mb-0">Current Order</h5>
                        </div>
                        <button className="btn btn-sm btn-outline-danger border-0" onClick={() => setCart([])} title="Clear Cart">
                            <i className="bi bi-trash3-fill"></i>
                        </button>
                    </div>

                    {/* Customer Selection */}
                    <div className="p-3 border-bottom bg-body-secondary bg-opacity-25">
                        {selectedCustomer ? (
                            <div className="d-flex justify-content-between align-items-center p-2 bg-white rounded-4 border">
                                <div className="d-flex align-items-center gap-2">
                                    <div className="bg-primary bg-opacity-10 p-2 rounded-3 text-primary"><i className="bi bi-person-fill"></i></div>
                                    <div className="lh-1">
                                        <div className="fw-bold small">{selectedCustomer.name}</div>
                                        <small className="text-muted" style={{fontSize: '0.7rem'}}>{selectedCustomer.phone}</small>
                                    </div>
                                </div>
                                <button className="btn btn-xs btn-light" onClick={() => setSelectedCustomer(null)}>Change</button>
                            </div>
                        ) : (
                            <div className="position-relative">
                                <input 
                                    type="text" 
                                    className="form-control form-control-sm rounded-pill ps-3" 
                                    placeholder="Search or add customer..." 
                                    value={customerSearch} 
                                    onChange={e => setCustomerSearch(e.target.value)} 
                                />
                                {customers.length > 0 && customerSearch && (
                                    <div className="list-group position-absolute w-100 shadow-lg" style={{zIndex: 1000, top: '40px'}}>
                                        {customers.map(c => (<button key={c.id} className="list-group-item list-group-item-action py-2" onClick={() => { setSelectedCustomer(c); setCustomerSearch(''); setCustomers([]); }}>{c.name} ({c.phone})</button>))}
                                    </div>
                                )}
                                <button className="btn btn-primary btn-sm rounded-circle position-absolute top-50 end-0 translate-middle-y me-1 p-0 d-flex align-items-center justify-content-center" style={{width: '24px', height: '24px'}} onClick={() => setShowNewCustomerModal(true)}>
                                    <i className="bi bi-plus"></i>
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Cart Items List */}
                    <div className="flex-grow-1 overflow-auto p-3 custom-scrollbar">
                        {cart.length > 0 ? (
                            cart.map(item => (
                                <div key={item.product_id} className="d-flex gap-3 mb-3 p-2 rounded-4 hover-bg">
                                    <div className="bg-body-secondary p-2 rounded-3 d-flex align-items-center justify-content-center" style={{width: '50px', height: '50px'}}>📦</div>
                                    <div className="flex-grow-1">
                                        <div className="d-flex justify-content-between">
                                            <h6 className="mb-0 fw-bold text-truncate" style={{maxWidth: '140px'}}>{item.name}</h6>
                                            <span className="fw-bold">Rs. {(item.price * item.quantity).toFixed(0)}</span>
                                        </div>
                                        <div className="d-flex justify-content-between align-items-center mt-2">
                                            <div className="btn-group border rounded-pill overflow-hidden" style={{height: '28px'}}>
                                                <button className="btn btn-xs btn-light border-0 px-2" onClick={() => updateQuantity(item.product_id, -1)}><i className="bi bi-dash"></i></button>
                                                <span className="px-3 small d-flex align-items-center fw-bold">{item.quantity}</span>
                                                <button className="btn btn-xs btn-light border-0 px-2" onClick={() => updateQuantity(item.product_id, 1)}><i className="bi bi-plus"></i></button>
                                            </div>
                                            <button className="btn btn-xs text-danger p-0" onClick={() => removeFromCart(item.product_id)}><i className="bi bi-x-circle-fill"></i></button>
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="text-center py-5 opacity-25">
                                <i className="bi bi-cart4 fs-1"></i>
                                <p className="mt-2">Cart is empty</p>
                            </div>
                        )}
                    </div>

                    {/* Order Summary & Checkout */}
                    <div className="p-4 bg-body-tertiary border-top mt-auto">
                        <div className="d-flex justify-content-between mb-2">
                            <span className="text-muted">Subtotal</span>
                            <span className="fw-bold">Rs. {calculateSubtotal()}</span>
                        </div>
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <span className="text-muted">Discount</span>
                            <div className="input-group input-group-sm" style={{ width: '120px' }}>
                                <span className="input-group-text bg-transparent border-0 pe-0 text-success fw-bold">- Rs.</span>
                                <input 
                                    type="number" 
                                    className="form-control form-control-sm border-0 border-bottom text-end text-success fw-bold bg-transparent shadow-none" 
                                    value={discount} 
                                    onChange={e => setDiscount(e.target.value)}
                                    placeholder="0"
                                />
                            </div>
                        </div>
                        
                        <div className="mb-4">
                            <label className="form-label small fw-bold text-muted text-uppercase mb-2">Payment Method</label>
                            <div className="pos-payment-selector">
                                <button className={`payment-option ${paymentMethod === 'cash' ? 'active' : ''}`} onClick={() => setPaymentMethod('cash')}>CASH</button>
                                <button className={`payment-option ${paymentMethod === 'partial' ? 'active' : ''}`} onClick={() => setPaymentMethod('partial')}>PARTIAL</button>
                                <button className={`payment-option ${paymentMethod === 'credit' ? 'active' : ''}`} onClick={() => setPaymentMethod('credit')}>CREDIT</button>
                            </div>
                            {paymentMethod === 'partial' && (
                                <div className="mt-3">
                                    <input type="number" className="form-control form-control-lg rounded-4 mb-2" placeholder="Enter Amount Paid" value={paidAmount} onChange={e => setPaidAmount(e.target.value)} />
                                    <div className="d-flex gap-2">
                                        {[500, 1000, 5000].map(amt => (
                                            <button key={amt} className="btn btn-sm btn-outline-primary rounded-pill px-3" onClick={() => setPaidAmount(amt)}>Rs. {amt}</button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <div>
                                <small className="text-muted d-block">TOTAL PAYABLE</small>
                                <h3 className="fw-bold mb-0 text-primary">Rs. {calculateTotal()}</h3>
                            </div>
                            <button 
                                className="pos-checkout-btn d-flex align-items-center justify-content-center gap-2" 
                                style={{width: 'auto', minWidth: '160px'}}
                                onClick={handleCheckout} 
                                disabled={loading || cart.length === 0}
                            >
                                {loading ? <span className="spinner-border spinner-border-sm"></span> : <><i className="bi bi-check2-circle fs-4"></i> PAY NOW</>}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Mobile Cart Toggle */}
                <div className="d-custom-drawer-mode position-fixed bottom-0 start-0 w-100 p-3 bg-white border-top shadow-lg" style={{zIndex: 1030}}>
                    <button className="btn btn-primary w-100 py-3 rounded-4 fw-bold d-flex justify-content-between align-items-center" onClick={() => setActiveTab(activeTab === 'products' ? 'cart' : 'products')}>
                        <span>{activeTab === 'products' ? <><i className="bi bi-receipt me-2"></i>View Bill</> : <><i className="bi bi-arrow-left me-2"></i>Back to Products</>}</span>
                        <span className="badge bg-white text-primary rounded-pill px-3">{cart.length} Items</span>
                    </button>
                </div>
            </div>
            
            {showNewCustomerModal && (
                <>
                    <div className="modal-backdrop fade show" style={{zIndex: 1100}}></div>
                    <div className="modal fade show d-block" style={{zIndex: 1101}} tabIndex="-1">
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content border-0 shadow-lg" style={{borderRadius: '24px'}}>
                                <div className="modal-header border-0 p-4 pb-0">
                                    <h5 className="modal-title fw-bold">New Customer</h5>
                                    <button type="button" className="btn-close" onClick={() => setShowNewCustomerModal(false)}></button>
                                </div>
                                <div className="modal-body p-4">
                                    <div className="mb-3"><label className="form-label small fw-bold">Name</label><input type="text" className="form-control" value={newCustomer.name} onChange={e => setNewCustomer({...newCustomer, name: e.target.value})}/></div>
                                    <div className="mb-3"><label className="form-label small fw-bold">Phone</label><input type="text" className="form-control" value={newCustomer.phone} onChange={e => setNewCustomer({...newCustomer, phone: e.target.value})}/></div>
                                    <div className="mb-3">
                                        <label className="form-label small fw-bold">Address</label>
                                        <textarea 
                                            className="form-control" 
                                            value={newCustomer.address} 
                                            onChange={e => setNewCustomer({...newCustomer, address: e.target.value})}
                                            style={{ height: '100px', resize: 'none' }}
                                        ></textarea>
                                    </div>
                                </div>
                                <div className="modal-footer border-0 p-4 pt-0">
                                    <button type="button" className="btn btn-light rounded-pill px-4" onClick={() => setShowNewCustomerModal(false)}>Cancel</button>
                                    <button type="button" className="btn btn-primary rounded-pill px-4" onClick={handleCreateCustomer}>Save & Select</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </Layout>
    );
};

export default POS;
